import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { memo, useCallback } from "react";
import { type RunBlocker } from "../bench-report-model";

const styles = {
  alert: {
    "& .MuiAlert-message": { width: "100%" },
  } satisfies SxProps<Theme>,
  title: { fontSize: "1.25rem", fontWeight: 600 } satisfies SxProps<Theme>,
  entry: {
    display: "flex",
    alignItems: "baseline",
    gap: 1,
    flexWrap: "wrap",
  } satisfies SxProps<Theme>,
  reason: { fontSize: "1.1rem", fontWeight: 500 } satisfies SxProps<Theme>,
};

type BlockerEntryProps = {
  readonly blocker: RunBlocker;
  readonly onOpenOrder: (orderUuid: string) => void;
};

const BlockerEntry = memo(({ blocker, onOpenOrder }: BlockerEntryProps) => {
  const handleOpen = useCallback(() => {
    onOpenOrder(blocker.orderUuid);
  }, [blocker.orderUuid, onOpenOrder]);
  return (
    <Box sx={styles.entry}>
      <Button
        color="error"
        size="small"
        variant="outlined"
        onClick={handleOpen}
      >
        {blocker.orderLabel}
      </Button>
      <Typography component="span" fontWeight={600}>
        {blocker.subject}:
      </Typography>
      <Typography component="span" sx={styles.reason}>
        {blocker.reason}
      </Typography>
    </Box>
  );
});
BlockerEntry.displayName = "BlockerEntry";

type RunBlockersProps = {
  readonly blockers: RunBlocker[];
  readonly runError: string | null;
  readonly onOpenOrder: (orderUuid: string) => void;
};

/**
 * Every reason the run could not send something, above the order tabs and in
 * larger type, so the operator reads it without opening each order. Each
 * entry's button opens that order's tab.
 */
const RunBlockers = ({ blockers, runError, onOpenOrder }: RunBlockersProps) => {
  if (blockers.length === 0 && runError === null) {
    return null;
  }
  return (
    <Alert severity="error" sx={styles.alert}>
      <AlertTitle sx={styles.title}>
        {runError === null
          ? `${blockers.length} thing(s) could not go out`
          : "The run stopped with an error"}
      </AlertTitle>
      <Stack spacing={1}>
        {runError !== null && (
          <Typography sx={styles.reason}>{runError}</Typography>
        )}
        {blockers.map((blocker) => (
          <BlockerEntry
            key={blocker.key}
            blocker={blocker}
            onOpenOrder={onOpenOrder}
          />
        ))}
      </Stack>
    </Alert>
  );
};

export default RunBlockers;
